import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AutoOrderState, BenefitHistoryType} from "../../../core/helpers/enums";
import {AutoOrderService} from "../../../core/service/auto-order.service";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {AutoOrderDto, AutoOrderResponse} from "../../../core/responses/AutoOrderResponse";
import {Patch} from "../../../core/models/patch";
import {ToastrService} from "ngx-toastr";
import {TranslatePipe} from "@ngx-translate/core";
import {AutoOrderPostModel} from "../../../core/models/auto-order";
import {OrderStatus} from "../../../core/helpers/constants";

@Component({
  selector: 'app-auto-order',
  templateUrl: './auto-order.component.html',
  styleUrls: ['./auto-order.component.scss'],
  providers: [TranslatePipe]
})

export class AutoOrderComponent implements OnInit, OnDestroy {

  @Input() userId: string;
  @Input() benefitId: number;
  @Input() benefitName: string;
  @Input() beneficiaryId?: number;
  @Input() billingAddressId?: number;
  @Input() shippingAddressId?: number;
  @Input() historyType?: BenefitHistoryType;
  @Input() orderStatus?: number;

  autoOrder: AutoOrderDto;
  autoOrderState: AutoOrderState;
  hasDynamicQuantity: boolean = false;
  mealTicketsOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  selectedQuantity = 1;
  maxNumberOfUnits = 24;
  stateOptions = [
    {label: 'HISTORY.AUTO_ORDER.AUTO_ORDER_STATUS.ACTIVE', value: AutoOrderState.Active},
    {label: 'HISTORY.AUTO_ORDER.AUTO_ORDER_STATUS.INACTIVE', value: AutoOrderState.Inactive}
  ];

  private ngUnsubscribe = new Subject();

  constructor(private autoOrderService: AutoOrderService,
              private toastrService: ToastrService,
              private translate: TranslatePipe) {
  }

  ngOnInit(): void {
    this.getAutoOrder();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  protected readonly AutoOrderState = AutoOrderState;

  private getAutoOrder() {
    this.autoOrderService.getAutoOrder(this.userId, this.benefitId, this.beneficiaryId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: AutoOrderResponse) => {
          this.onGetAutoOrderSuccess(data);
        },
        error: (err) => {
          this.onGetAutoOrderFail(err);
        }
      });
  }

  private onGetAutoOrderSuccess(data: AutoOrderResponse) {
    this.autoOrder = data.responseObject;
    if (this.autoOrder !== null) {
      this.autoOrderState = this.autoOrder.isActive === true ? AutoOrderState.Active : AutoOrderState.Inactive;
      this.hasDynamicQuantity = this.autoOrder.hasDynamicQuantity;
      this.selectedQuantity = this.autoOrder.noOfUnits ?? 1;
    } else {
      this.autoOrderState = AutoOrderState.Inactive
    }
  }

  private onGetAutoOrderFail(err: any) {

  }

  get isRestrictedByOrderStatus(): boolean {
    return this.historyType === BenefitHistoryType.Order
      && this.orderStatus != null
      && this.orderStatus !== OrderStatus.Created;
  }

  hasChanges(): boolean {
    if (this.autoOrder === null) {
      return true;
    }

    const noOfUnitsChanged = (this.autoOrder.noOfUnits ?? 1) !== this.selectedQuantity;
    const isActiveChanged = this.autoOrder.isActive !== (this.autoOrderState === AutoOrderState.Active);
    const hasDynamicChanged = this.autoOrder.hasDynamicQuantity !== this.hasDynamicQuantity;

    return noOfUnitsChanged || isActiveChanged || hasDynamicChanged;
  }

  saveOptions() {
    if (this.autoOrder !== null) {
      //call update
      const patchData: Array<Patch> = [];

      if (this.autoOrder.noOfUnits !== this.selectedQuantity) {
        patchData.push({
          op: 'replace',
          path: 'noOfUnits',
          value: this.hasDynamicQuantity ? null : this.selectedQuantity.toString()
        });
      }

      const isActive = this.autoOrderState === AutoOrderState.Active;
      if (this.autoOrder.isActive !== isActive) {
        patchData.push({
          op: 'replace',
          path: 'isActive',
          value: isActive.toString()
        });
      }

      if (this.autoOrder.hasDynamicQuantity !== this.hasDynamicQuantity) {
        patchData.push({
          op: 'replace',
          path: 'hasDynamicQuantity',
          value: this.hasDynamicQuantity.toString()
        });
      }

      if (patchData.length > 0) {
        this.autoOrderService.updateAutoOrder(this.autoOrder.id, patchData)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: () => {
              this.toastrService.success(
                this.translate.transform('DETAILS.MESSAGES.AUTO_ORDER_STATUS_UPDATE_SUCCESS'),
                this.translate.transform('SUCCESS'),
                {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
              );
              this.getAutoOrder();
            },
            error: (err: any) => {
              err.messages.forEach((errorItem) => {
                this.toastrService.error(
                  this.translate.transform(errorItem),
                  this.translate.transform('ERROR'),
                  {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
                );
              });
            }
          });
      }
    } else {
      //call create
      const postModel: AutoOrderPostModel = {
        userId: this.userId,
        beneficiaryId: this.beneficiaryId > 0 ? this.beneficiaryId : null,
        benefitId: this.benefitId,
        hasDynamicQuantity: this.hasDynamicQuantity,
        noOfUnits: this.hasDynamicQuantity ? null : this.selectedQuantity,
        billingAddressId: this.billingAddressId,
        shippingAddressId: this.shippingAddressId
      }

      this.autoOrderService.createAutoOrder(postModel)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translate.transform('DETAILS.MESSAGES.AUTO_ORDER_STATUS_UPDATE_SUCCESS'),
              this.translate.transform('SUCCESS'),
              {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
            );
            this.getAutoOrder();
          },
          error: (err: any) => {
            err.messages.forEach((errorItem) => {
              this.toastrService.error(
                this.translate.transform(errorItem),
                this.translate.transform('ERROR'),
                {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
              );
            });
          }
        });
    }
  }
}
